/**
 * Pre-release ("Coming Soon") gate for Quick Add — RR-227.
 *
 * The PDP reads `custom.availability_status` directly in Liquid
 * (`blocks/product-page/_form.liquid`, `sections/product/product-sticky-atc.liquid`)
 * and swaps Add to Bag for the Klaviyo notify form. Every Quick Add surface is
 * driven by Athos result data instead, which only carries stock — verified
 * against site `at2ysw` on 2026-08-11, where the Coming Soon product
 * `arabella-dress-raspberry` returns `available: "true"`, `ss_available: "1"`
 * and a genuinely in-stock size 6. So Quick Add rendered a working Add button
 * and the item went into the cart.
 *
 * This module is the single source of truth for that gate:
 * - `hydrateAvailability` is fired (not awaited) from the normalisers, so cards
 *   render at full speed and the status arrives a moment later.
 * - `whenAvailabilityKnown` is awaited on the add path, closing the window
 *   where a click lands before hydration resolves. It reuses the in-flight
 *   promise, so it costs no extra request.
 *
 * When the AMC feed eventually exposes `mfield_custom_availability_status` (the
 * same route that added `ss_sizes` on 2026-07-30), `seedFromResult` below can
 * read it and the Storefront round trip disappears on its own — nothing else
 * needs to change.
 */

import { getProductAvailability } from 'libs/storefront/products'

interface Availability {
  /** Handleized status, e.g. `coming-soon`. `null` when the product has none. */
  status: string | null
  /** ISO 8601 from the `date_time` metafield, or `null`. */
  releaseDate: string | null
}

const COMING_SOON = 'coming-soon'

/** Handles per request. The lookup is one aliased `product(handle:)` per handle
 *  at a measured 3 query-cost each (36 for a 12-handle batch, 2026-08-11), so 50
 *  puts a full batch near 150 against the Storefront API's 1000-point bucket,
 *  leaving room for the theme's other queries. Grids fit in a single batch; only
 *  a large wishlist splits. */
const BATCH_SIZE = 50

const resolved = new Map<string, Availability>()
const inFlight = new Map<string, Promise<void>>()

/** Liquid's `handleize`, near enough for comparing a fixed set of metafield
 *  choices: "Coming Soon" → `coming-soon`. */
function handleize(value: unknown): string | null {
  const slug = String(value ?? '')
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
  return slug || null
}

async function fetchBatch(handles: string[]): Promise<void> {
  try {
    const rows = await getProductAvailability(handles)
    const byHandle = new Map((rows ?? []).map((row) => [row.handle, row]))

    for (const handle of handles) {
      const row = byHandle.get(handle)
      // Handles the API didn't return (unpublished, renamed, outside the
      // storefront's publication) resolve to "no status" rather than staying
      // unknown — an unresolved handle would re-query on every render.
      resolved.set(handle, {
        status: handleize(row?.availabilityStatus),
        releaseDate: row?.releaseDate ?? null
      })
    }
  } catch (error) {
    console.warn('[availability] lookup failed', error)
    // Fail open. A Storefront outage must not disable Quick Add store-wide;
    // the PDP still gates Coming Soon correctly in Liquid either way.
    for (const handle of handles) {
      resolved.set(handle, { status: null, releaseDate: null })
    }
  } finally {
    for (const handle of handles) inFlight.delete(handle)
  }
}

/**
 * Look up Availability Status for any of `handles` not already known or in
 * flight. Safe to call on every render — repeat handles are free.
 *
 * Fire-and-forget from the normalisers: the returned promise exists for the
 * add path, not to block rendering.
 */
export function hydrateAvailability(handles: Array<string | undefined | null>): Promise<void> {
  const pending = [...new Set(handles.filter(Boolean) as string[])].filter(
    (handle) => !resolved.has(handle) && !inFlight.has(handle)
  )
  if (!pending.length) return Promise.resolve()

  const runs: Array<Promise<void>> = []
  for (let index = 0; index < pending.length; index += BATCH_SIZE) {
    const batch = pending.slice(index, index + BATCH_SIZE)
    const run = fetchBatch(batch)
    // Registered synchronously — `fetchBatch` only reaches its first `await`
    // (and therefore only clears these) in a later microtask.
    for (const handle of batch) inFlight.set(handle, run)
    runs.push(run)
  }

  return Promise.all(runs).then(() => undefined)
}

/**
 * Resolves once `handle`'s status is known, reusing the in-flight request when
 * hydration is already under way and starting one only if it never ran.
 */
export function whenAvailabilityKnown(handle?: string | null): Promise<void> {
  if (!handle) return Promise.resolve()
  if (resolved.has(handle)) return Promise.resolve()
  return inFlight.get(handle) ?? hydrateAvailability([handle])
}

/**
 * Mirrors the PDP's `pre_release` calculation in `_form.liquid`: Coming Soon,
 * and either no Release date at all or one still in the future. A product whose
 * release date has passed is purchasable on the PDP even with a stale metafield,
 * so Quick Add must allow it too — otherwise the two surfaces disagree.
 *
 * Returns `false` while the status is still unknown; `whenAvailabilityKnown`
 * is what makes the add path wait for a definite answer.
 */
export function isPreRelease(handle?: string | null): boolean {
  if (!handle) return false
  const entry = resolved.get(handle)
  if (!entry || entry.status !== COMING_SOON) return false
  if (!entry.releaseDate) return true

  const release = Date.parse(entry.releaseDate)
  if (Number.isNaN(release)) return true
  return release > Date.now()
}
